import { StandardTabs } from '@cimplico/components';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useIsFirmRole } from '../../../hooks/use-is-firm-role';
import { useUserContext } from '../../../providers/UserProvider';
import { User } from '../../../types/user';
import WorkpapersTable from '../../workpapers/WorkpapersTable';
import UserNotifications from './user-notifications/UserNotifications';
import UserProfile from './user-profile/UserProfile';
import UserOverviewFirmInsights from './firm-insights/UserOverviewFirmInsights';
import ResetMFA from './user-profile/ResetMFA';
import RemoveUsers from './RemoveUsers';

type UserSettingsProps = {
    user: User;
};

function getTabs(isCurrentUser: boolean, isAdmin: boolean): string[] {
    if (isCurrentUser) {
        return ['profile', 'insights', 'workpapers', 'notifications'];
    } else {
        if (isAdmin) {
            return ['actions', 'insights', 'workpapers'];
        } else {
            return ['insights', 'workpapers'];
        }
    }
}

function UserSettings({ user }: UserSettingsProps): JSX.Element {
    const nonSSO = user.auth0Id.startsWith('auth0|');
    const { id } = useUserContext();
    const { isAdmin } = useIsFirmRole();

    const isCurrentUser = user.id === id;
    const tabs = getTabs(isCurrentUser, isAdmin);

    const [params, setParams] = useSearchParams();
    const [tab, setTab] = useState(getSavedTab(tabs, params.get('tab')));

    const workpapersFilter = `assigned=${user.id}&status=ACTIVE`;

    function getSavedTab(tabs: string[], tab: string | null): number {
        if (!tab) {
            return 0;
        }

        return tabs.includes(tab) ? tabs.indexOf(tab) : 0;
    }

    function handleTabChange(t: number): void {
        setTab(t);
        setParams({ tab: tabs[t] });
    }

    return (
        <>
            <div className="border border-gray-200 dark:border-white/[0.07] rounded-lg w-full pb-4 grid gap-y-4 bg-white dark:bg-gray-800">
                <div className="border-b border-gray-200 dark:border-white/[0.07] px-8">
                    <StandardTabs
                        tabs={
                            isCurrentUser
                                ? [{ label: 'Profile' }, { label: 'Insights' }, { label: 'Open Workpapers' }, { label: 'Notifications' }]
                                : isAdmin
                                ? [{ label: 'Actions' }, { label: 'Insights' }, { label: 'Open Workpapers' }]
                                : [{ label: 'Insights' }, { label: 'Open Workpapers' }]
                        }
                        onChange={handleTabChange}
                        defaultIndex={tab}
                    />
                </div>
                <div className="px-8 min-h-[300px]">
                    {isCurrentUser && (
                        <>
                            {tab === 0 && <UserProfile user={user} />}
                            {tab === 1 && <UserOverviewFirmInsights />}
                            {tab === 2 && <WorkpapersTable filter={workpapersFilter} />}
                            {tab === 3 && <UserNotifications />}
                        </>
                    )}

                    {!isCurrentUser && !isAdmin && (
                        <>
                            {tab === 0 && <UserOverviewFirmInsights />}
                            {tab === 1 && <WorkpapersTable filter={workpapersFilter} />}
                        </>
                    )}

                    {!isCurrentUser && isAdmin && (
                        <>
                            {tab === 0 && (
                                <>
                                    <RemoveUsers user={user} />
                                    {nonSSO && <ResetMFA user={user} />}
                                </>
                            )}
                            {tab === 1 && <UserOverviewFirmInsights />}
                            {tab === 2 && <WorkpapersTable filter={workpapersFilter} />}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default UserSettings;
